import { domain, schemas } from 'modules/data';
import { integrationActions, loadXeroIntegrationStep2Sequence } from 'modules/integration';
import { normalize } from 'normalizr';
import { api } from 'services/api';
import { routingService } from 'services/routing';
import { getPath, Path } from 'urlBuilder';

import { OAuthContext } from '../types';
import { getFinalUrl } from './getFinalUrl';

export const handleXeroIntegration = async (ctx: OAuthContext) => {
    const { code, backendState, company, dispatch, invalidateCompanyData, uiState } = ctx;

    if (uiState.type !== 'oauth') return;

    // We first request exchange codes to get a list of available Xero tenant ids
    // TODO: Should be moved to the react-query hooks in the future
    const { tenants: tenantsBackend } = await api.companies.exchangeCodeForAccessToken({
        state: backendState,
        code,
    });

    const users: domain.User[] = [];
    const tenants = tenantsBackend.map((tenant): domain.XeroTenantInfo => {
        let owner: domain.User | undefined;

        if (tenant.owner) {
            const { entities } = normalize(tenant.owner, schemas.userSchema);

            owner = Object.values(entities?.users || {})[0];
            users.push(owner);
        }

        return {
            ...tenant,
            owner: owner?.id,
        };
    });

    if (!company || !company.integration) {
        // First-time connection OR new company
        dispatch(
            loadXeroIntegrationStep2Sequence({
                companyId: company?.id || null,
                tenants,
                users,
                backendState,
            })
        );

        if (company) {
            routingService.replace(getPath(Path.companyWorkflows, company.id));
        } else {
            routingService.replaceToDefaultPath();
        }

        return;
    }

    // Reconnection flow
    const currentTenantId = company.integration.integratedCompanyId;
    const hasAccessToCurrentCompany = tenants.some(({ tenantId }) => tenantId === currentTenantId);

    if (hasAccessToCurrentCompany) {
        dispatch(
            integrationActions.completeIntegrationAuth(
                {
                    integrationType: domain.IntegrationType.Xero,
                    newCompany: false,
                    companyId: company.id,
                    state: backendState,
                    tenantId: currentTenantId,
                },
                invalidateCompanyData
            )
        );
    } else {
        // if we did not get access from Xero to the company, we want to reconnect
        dispatch(integrationActions.showCompanyMismatchPopup(domain.IntegrationType.Xero));
    }

    routingService.replace(getFinalUrl(uiState, company.id));
};
